import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Video } from 'src/app/interfaces/video/video';
import { VideoSubtitle } from 'src/app/interfaces/video/video-subtitle';
import { ApiService } from 'src/app/services/api.service';
import { ToastService } from 'src/app/services/toast.service';
import { VideoEditService } from '../video-edit.service';
import { VideoService } from 'src/app/services/video.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ReplaceVideoComponent } from '../replace-video/replace-video.component';

@Component({
  selector: 'app-advanced-setting-form',
  templateUrl: './advanced-setting-form.component.html',
  styleUrls: ['./advanced-setting-form.component.scss'],
})
export class AdvancedSettingFormComponent implements OnInit, OnDestroy {
  @Output('touch') touch: EventEmitter<boolean> = new EventEmitter();
  @Input('video') video: Video | any;
  videoSpacesLink: string = '';
  isLoadingLink: boolean = false;
  isDownloading: boolean = false;

  // null mientras carga o si la API de subtítulos no responde (se oculta la sección)
  subtitles: VideoSubtitle[] | null = null;
  isGeneratingSubtitles: boolean = false;

  private processingPollInterval: any | null = null;
  private subtitlesPollInterval: any | null = null;
  private readonly PROCESSING_POLL_MS = 20000;

  constructor(
    private api: ApiService,
    private toastr: ToastService,
    public videoEditService: VideoEditService,
    private videoService: VideoService,
    private ngbModal: NgbModal,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.videoEditService.source.enable_watermark = false;
    this.loadVideoSpacesLink();
    if (this.isProcessingNewVideo()) {
      this.startProcessingPoll();
    }
    this.loadSubtitles();
  }

  ngOnDestroy(): void {
    this.stopProcessingPoll();
    this.stopSubtitlesPoll();
  }

  touched() {
    this.touch.emit(true);
  }

  save() {
    return this.api
      .put('/video/' + this.video?.id + '/source', this.videoEditService.source)
      .then((response) => {
        if (response.result && response.result.origin_id) {
          this.videoEditService.source.origin.id = response.result.origin_id;
        }
        this.touch.emit(false);
      })
      .catch((response) => {
        this.toastr.showError('something went wrong updating clip source');
      });
  }

  loadVideoSpacesLink(): void {
    this.isLoadingLink = true;
    this.videoService
      .getVideoURL(this.video.source.video_spaces_original)
      .then((resp) => {
        this.videoSpacesLink = resp?.url || '';
      })
      .catch((error) => {
        console.error('Error loading video spaces link', error);
        // Fallback: construir el link con los datos locales
        this.videoSpacesLink = ``;
        this.isLoadingLink = false;
      });
  }

  getVideoSpacesLink(): string {
    return this.videoSpacesLink;
  }

  openVideoSpacesLink(): void {
    if (this.videoSpacesLink) {
      window.open(this.videoSpacesLink, '_blank');
    }
  }

  downloadVideo(): void {
    if (!this.videoSpacesLink) return;
    this.isDownloading = true;
    fetch(this.videoSpacesLink)
      .then(res => res.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.video?.title || 'video';
        a.click();
        URL.revokeObjectURL(url);
      })
      .finally(() => {
        this.isDownloading = false;
      });
  }

  hasVideoSpacesLink(): boolean {
    return (
      !!this.videoEditService?.source?.video_spaces_original &&
      !!this.videoEditService?.source?.external_id
    );
  }

  isProcessingNewVideo(): boolean {
    const source = this.video?.source;
    if (!source) return false;
    return source.video_provider === 'vixy' && source.spaces_upload === false;
  }

  openReplaceModal(): void {
    const modalRef = this.ngbModal.open(ReplaceVideoComponent, {
      windowClass: 'default-modal',
      size: 'md',
      centered: false,
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.selfModal = modalRef;
    modalRef.componentInstance.video = this.video;
    modalRef.componentInstance.replaced.subscribe(() => {
      this.refreshVideo();
    });
  }

  private refreshVideo(): Promise<void> {
    return this.videoService
      .getById(this.video.id)
      .then((res: any) => {
        const updated = res?.data?.video;
        if (updated?.source) {
          this.video.source = updated.source;
        }
        if (this.isProcessingNewVideo()) {
          this.videoSpacesLink = '';
          this.startProcessingPoll();
        } else {
          this.stopProcessingPoll();
          this.loadVideoSpacesLink();
        }
      })
      .catch(() => {
        this.loadVideoSpacesLink();
      });
  }

  private startProcessingPoll(): void {
    if (this.processingPollInterval) return;
    this.processingPollInterval = setInterval(() => {
      this.videoService
        .getById(this.video.id)
        .then((res: any) => {
          const updated = res?.data?.video;
          if (updated?.source) {
            this.video.source = updated.source;
          }
          if (!this.isProcessingNewVideo()) {
            this.stopProcessingPoll();
            this.loadVideoSpacesLink();
          }
        })
        .catch(() => {
          // keep polling on transient errors
        });
    }, this.PROCESSING_POLL_MS);
  }

  private stopProcessingPoll(): void {
    if (this.processingPollInterval) {
      clearInterval(this.processingPollInterval);
      this.processingPollInterval = null;
    }
  }

  readySubtitles(): VideoSubtitle[] {
    return (this.subtitles || []).filter((s) => s.status === 'ready');
  }

  subtitlesInProgress(): boolean {
    return (this.subtitles || []).some((s) => s.status === 'pending' || s.status === 'processing');
  }

  subtitlesFailed(): boolean {
    return !this.subtitlesInProgress() && (this.subtitles || []).some((s) => s.status === 'failed');
  }

  // Videos antiguos sin subtítulos: la API encola la generación (Whisper + traducción)
  generateSubtitles(): void {
    this.isGeneratingSubtitles = true;
    this.videoService
      .generateSubtitles(this.video.id)
      .then(() => {
        this.toastr.showSuccess(this.translate.instant('video:subtitles:queued'));
      })
      .catch((error) => {
        // 409: ya se estaban generando o ya existen; se recarga el estado sin error
        if (error?.status === 422) {
          this.toastr.showError(this.translate.instant('video:subtitles:error:processing'));
        } else if (error?.status !== 409) {
          this.toastr.showError(this.translate.instant('video:subtitles:error'));
        }
      })
      .finally(() => {
        this.isGeneratingSubtitles = false;
        this.loadSubtitles();
      });
  }

  private loadSubtitles(): Promise<void> {
    return this.videoService.getSubtitlesStatus(this.video.id).then((subtitles) => {
      this.subtitles = subtitles;
      if (this.subtitlesInProgress()) {
        this.startSubtitlesPoll();
      } else {
        this.stopSubtitlesPoll();
      }
    });
  }

  private startSubtitlesPoll(): void {
    if (this.subtitlesPollInterval) return;
    this.subtitlesPollInterval = setInterval(() => this.loadSubtitles(), this.PROCESSING_POLL_MS);
  }

  private stopSubtitlesPoll(): void {
    if (this.subtitlesPollInterval) {
      clearInterval(this.subtitlesPollInterval);
      this.subtitlesPollInterval = null;
    }
  }
}
